import React, { useState, useEffect, useCallback } from 'react';
import { getAuth, deleteUser } from 'firebase/auth';
import { getDatabase, ref as dbRef, get, update, set, remove } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { app } from '../../firebase';
import { toast } from 'react-toastify';
import {
  FaExclamationTriangle, FaPlus, FaTrash, FaExclamationCircle,
  FaFileAlt, FaDownload, FaSpinner, FaUser, FaEnvelope, FaPhone,
  FaMapMarkerAlt, FaBriefcase, FaCalendar, FaBuilding, FaGraduationCap,
  FaImage, FaVideo
} from 'react-icons/fa';

// Constants
const MAX_RESUME_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_PHOTO_SIZE = 5 * 1024 * 1024;   // 5MB

// Comprehensive list of industries
const INDUSTRIES_LIST = [
  "Information Technology", "Software Development", "Cybersecurity", "Data Science & Analytics",
  "Artificial Intelligence & Machine Learning", "Cloud Computing", "Web Development",
  "Mobile App Development", "IT Support & Services", "Telecommunications",
  "Banking & Financial Services", "Accounting & Auditing", "Insurance",
  "Investment & Asset Management", "Real Estate", "Consulting", "Human Resources",
  "Marketing & Advertising", "Public Relations", "Sales & Business Development",
  "Healthcare & Medical Services", "Nursing", "Pharmaceuticals", "Biotechnology",
  "Mental Health Services", "Dental Services", "Medical Research", "Health Administration",
  "Education & Teaching", "Higher Education", "Early Childhood Education",
  "Corporate Training", "E-Learning & EdTech", "Special Education",
  "Civil Engineering", "Mechanical Engineering", "Electrical Engineering",
  "Chemical Engineering", "Manufacturing", "Automotive", "Aerospace", "Construction",
  "Graphic Design", "Film & Video Production", "Photography", "Music & Audio",
  "Writing & Editing", "Journalism", "Broadcasting", "Gaming & Interactive Media",
  "Hotel & Resort Management", "Restaurant & Food Service", "Travel & Tourism",
  "Event Planning", "Cruise & Maritime",
  "Retail Management", "E-Commerce", "Customer Service", "Fashion & Apparel", "Beauty & Cosmetics",
  "Agriculture & Farming", "Environmental Services", "Renewable Energy",
  "Wildlife & Conservation", "Forestry", "Fishing & Aquaculture",
  "Legal Services", "Government & Public Administration", "Law Enforcement",
  "Military & Defense", "Non-Profit & NGO", "Social Services",
  "Logistics & Supply Chain", "Shipping & Freight", "Aviation",
  "Public Transportation", "Warehousing & Distribution",
  "Plumbing", "Electrical Work", "Carpentry", "HVAC", "Welding",
  "Masonry", "Auto Mechanics", "General Contracting",
  "Security Services", "Domestic Work & Housekeeping", "Childcare & Nanny Services",
  "Pet Care & Veterinary", "Fitness & Wellness", "Sports & Recreation",
  "Religious & Spiritual Services", "Other"
];

const aboutMePlaceholder = `Share your professional story! Include:
• Your current role and years of experience
• Key areas of expertise and specializations
• Notable achievements and certifications
• What you're passionate about in your field
• Your career goals and aspirations
• Any unique skills or perspectives you bring
Keep it professional but let your personality shine through!`;

const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^/?]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^/?]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const ToggleSwitch = ({ isChecked, onChange, disabled }) => (
  <div className="flex items-center">
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={isChecked}
        onChange={onChange}
        disabled={disabled}
      />
      <div className={`
        w-11 h-6 rounded-full peer
        peer-focus:outline-none peer-focus:ring-4
        ${isChecked
          ? 'bg-green-500 peer-focus:ring-green-300'
          : 'bg-red-500 peer-focus:ring-red-300'
        }
        after:content-[''] after:absolute after:top-[2px] after:left-[2px]
        after:bg-white after:border-gray-300 after:border after:rounded-full
        after:h-5 after:w-5 after:transition-all
        ${isChecked ? 'after:translate-x-full' : 'after:translate-x-0'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}></div>
    </label>
    <span className={`ml-3 text-sm font-medium ${isChecked ? 'text-green-700' : 'text-red-700'}`}>
      {isChecked ? 'Public Profile' : 'Private Profile'}
    </span>
  </div>
);

const ProfileAndResume = () => {
  const [profileData, setProfileData] = useState({
    // Basic Profile fields
    firstName: '',
    middleInitial: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    employmentType: '',
    birthDate: '',
    gender: '',
    parish: '',
    labourAvailability: 'no',
    levelOfEducation: '',
    objectives: '',
    industriesOfInterest: [],
    isPublic: true,
    profileStatus: 'active',
    lastLoginAt: null,
    createdAt: null,
    updatedAt: null,
    // Resume/Profile fields (nested under profile in DB)
    profile: {
      aboutMe: '',
      skills: [],
      workExperience: [],
      education: [],
      resume: null,
      photo: null,
      video: { youtubeUrl: '' }
    }
  });

  const [files, setFiles] = useState({
    resume: null,
    photo: null
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [industrySearchTerm, setIndustrySearchTerm] = useState('');
  const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);
  const [missingFields, setMissingFields] = useState([]);

  const auth = getAuth(app);
  const db = getDatabase(app);
  const storage = getStorage(app);

  const fetchData = useCallback(async () => {
    try {
      if (!auth.currentUser) {
        toast.error('Please sign in to view your profile');
        setLoading(false);
        return;
      }

      const candidateRef = dbRef(db, `candidates/${auth.currentUser.uid}`);
      const snapshot = await get(candidateRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        setProfileData({
          firstName: data.firstName || '',
          middleInitial: data.middleInitial || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          employmentType: data.employmentType?.toLowerCase() || '',
          birthDate: data.birthDate || '',
          gender: data.gender?.toLowerCase() || '',
          parish: data.parish || '',
          labourAvailability: data.labourAvailability || 'no',
          levelOfEducation: data.levelOfEducation || '',
          objectives: data.objectives || '',
          industriesOfInterest: data.industriesOfInterest || [],
          isPublic: data.isPublic !== false,
          profileStatus: data.profileStatus || 'active',
          lastLoginAt: data.lastLoginAt || null,
          createdAt: data.createdAt || null,
          updatedAt: data.updatedAt || null,
          profile: {
            aboutMe: data.profile?.aboutMe || '',
            skills: data.profile?.skills || [],
            workExperience: data.profile?.workExperience || [],
            education: data.profile?.education || [],
            resume: data.profile?.resume || null,
            photo: data.profile?.photo || null,
            video: data.profile?.video || { youtubeUrl: '' }
          }
        });
      } else {
        // Create initial profile
        const currentDate = new Date();
        const initialData = {
          isPublic: true,
          createdAt: currentDate.toISOString(),
          updatedAt: currentDate.toISOString(),
          profileStatus: 'active',
          lastLoginAt: currentDate.toISOString()
        };
        await update(candidateRef, initialData);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading profile data');
      setLoading(false);
    }
  }, [auth.currentUser, db]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const validateForm = () => {
    const missing = [];

    if (!profileData.firstName?.trim()) missing.push('First Name');
    if (!profileData.lastName?.trim()) missing.push('Last Name');
    if (!profileData.email?.trim()) missing.push('Email');
    if (!profileData.phone?.trim()) missing.push('Phone');
    if (!profileData.parish) missing.push('Parish');
    if (!profileData.employmentType) missing.push('Employment Type');
    if (!files.resume && !profileData.profile?.resume?.url) missing.push('Resume Document');
    if (!profileData.profile?.aboutMe?.trim()) missing.push('About Me');
    if (!profileData.profile?.skills?.length) missing.push('At least one skill');

    setMissingFields(missing);
    return missing;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      profile: {
        ...prev.profile,
        [field]: value
      }
    }));
  };

  // Filter industries based on search term
  const filteredIndustries = INDUSTRIES_LIST.filter(industry =>
    industry.toLowerCase().includes(industrySearchTerm.toLowerCase())
  );

  const toggleIndustry = (industry) => {
    setProfileData(prev => {
      const isSelected = prev.industriesOfInterest.includes(industry);
      return {
        ...prev,
        industriesOfInterest: isSelected
          ? prev.industriesOfInterest.filter(i => i !== industry)
          : [...prev.industriesOfInterest, industry]
      };
    });
  };

  const removeIndustry = (industry) => {
    setProfileData(prev => ({
      ...prev,
      industriesOfInterest: prev.industriesOfInterest.filter(i => i !== industry)
    }));
  };

  const handlePrivacyToggle = async () => {
    try {
      const newPrivacyStatus = !profileData.isPublic;
      const candidateRef = dbRef(db, `candidates/${auth.currentUser.uid}`);

      await update(candidateRef, {
        isPublic: newPrivacyStatus,
        updatedAt: new Date().toISOString()
      });

      setProfileData(prev => ({
        ...prev,
        isPublic: newPrivacyStatus
      }));

      toast.success(newPrivacyStatus ?
        'Profile is now public' :
        'Profile is now private');
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  const handleDeleteProfile = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to permanently delete your profile and account? This cannot be undone.'
    );
    if (!confirmed) return;

    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      await remove(dbRef(db, `candidates/${userId}`));
      await deleteUser(auth.currentUser);

      toast.success('Your account has been deleted.');
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/requires-recent-login') {
        toast.error('Please sign out and sign back in before deleting your account.');
      } else {
        toast.error('Error deleting account. Please try again.');
      }
    }
  };

  // File handling
  const validateFile = (file, type) => {
    const maxSizes = {
      resume: MAX_RESUME_SIZE,
      photo: MAX_PHOTO_SIZE
    };

    if (!file) throw new Error('No file selected');
    if (file.size > maxSizes[type]) {
      throw new Error(`File size exceeds maximum allowed for ${type}`);
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      validateFile(file, type);
      setFiles(prev => ({ ...prev, [type]: file }));
      setMissingFields(prev => prev.filter(field => field !== 'Resume Document'));
    } catch (err) {
      toast.error(err.message);
      e.target.value = '';
    }
  };

  const handleFileUpload = async (file, type) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        toast.error('User not authenticated');
        return null;
      }

      const timestamp = Date.now();
      const storagePath = `${type}s/${userId}/${timestamp}_${file.name}`;
      const fileRef = storageRef(storage, storagePath);

      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      return {
        name: file.name,
        url,
        contentType: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString()
      };
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(`Failed to upload ${type}: ${err.message}`);
      return null;
    }
  };

  const clearFileSelection = (type) => {
    setFiles(prev => ({ ...prev, [type]: null }));
    const input = document.getElementById(`${type}-upload`);
    if (input) input.value = '';
  };

  // Skills handling
  const handleSkillAdd = () => {
    handleProfileChange('skills', [...(profileData.profile.skills || []), '']);
    setMissingFields(prev => prev.filter(field => field !== 'At least one skill'));
  };

  const handleSkillChange = (index, value) => {
    const newSkills = [...(profileData.profile.skills || [])];
    newSkills[index] = value;
    handleProfileChange('skills', newSkills);
  };

  const handleSkillDelete = (index) => {
    const newSkills = profileData.profile.skills.filter((_, i) => i !== index);
    handleProfileChange('skills', newSkills);
  };

  // Work Experience handling
  const handleWorkExperienceAdd = () => {
    handleProfileChange('workExperience', [
      ...(profileData.profile.workExperience || []),
      { company: '', position: '', startDate: '', endDate: '', description: '' }
    ]);
  };

  const handleWorkExperienceChange = (index, field, value) => {
    const newExp = [...(profileData.profile.workExperience || [])];
    newExp[index] = { ...newExp[index], [field]: value };
    handleProfileChange('workExperience', newExp);
  };

  const handleWorkExperienceDelete = (index) => {
    const newExp = profileData.profile.workExperience.filter((_, i) => i !== index);
    handleProfileChange('workExperience', newExp);
  };

  // Education handling
  const handleEducationAdd = () => {
    handleProfileChange('education', [
      ...(profileData.profile.education || []),
      { institution: '', degree: '', field: '', graduationYear: '' }
    ]);
  };

  const handleEducationChange = (index, field, value) => {
    const newEdu = [...(profileData.profile.education || [])];
    newEdu[index] = { ...newEdu[index], [field]: value };
    handleProfileChange('education', newEdu);
  };

  const handleEducationDelete = (index) => {
    const newEdu = profileData.profile.education.filter((_, i) => i !== index);
    handleProfileChange('education', newEdu);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missing = validateForm();
    if (missing.length > 0) {
      toast.error(`Please complete required fields: ${missing.join(', ')}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setSaving(true);
      const currentDate = new Date();
      const userId = auth.currentUser?.uid;

      if (!userId) {
        toast.error('User not authenticated');
        return;
      }

      // Check YouTube URL if provided
      if (profileData.profile.video?.youtubeUrl && !getYouTubeVideoId(profileData.profile.video.youtubeUrl)) {
        toast.error('Invalid YouTube URL format');
        setSaving(false);
        return;
      }

      // Upload new files if selected
      let updatedProfile = { ...profileData.profile };

      for (const [type, file] of Object.entries(files)) {
        if (file) {
          const fileData = await handleFileUpload(file, type);
          if (!fileData) {
            toast.error(`Failed to upload ${type}`);
            setSaving(false);
            return;
          }
          updatedProfile[type] = fileData;
        }
      }

      // Calculate expiry date (90 days from now)
      const expiryDate = new Date(currentDate);
      expiryDate.setDate(expiryDate.getDate() + 90);

      // Prepare the data to save
      const dataToSave = {
        firstName: profileData.firstName,
        middleInitial: profileData.middleInitial,
        lastName: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone,
        address: profileData.address,
        employmentType: profileData.employmentType,
        birthDate: profileData.birthDate,
        gender: profileData.gender,
        parish: profileData.parish,
        labourAvailability: profileData.labourAvailability,
        levelOfEducation: profileData.levelOfEducation,
        objectives: profileData.objectives,
        industriesOfInterest: profileData.industriesOfInterest,
        isPublic: profileData.isPublic,
        updatedAt: currentDate.toISOString(),
        createdAt: profileData.createdAt || currentDate.toISOString(),
        expiryDate: expiryDate.toISOString(),
        expirationNotificationSent: false,
        profile: {
          ...updatedProfile,
          updatedAt: currentDate.toISOString()
        }
      };

      const candidateRef = dbRef(db, `candidates/${userId}`);
      await set(candidateRef, {
        ...(await get(candidateRef)).val(),
        ...dataToSave
      });

      // Clear file selections
      setFiles({ resume: null, photo: null });
      document.querySelectorAll('input[type="file"]').forEach(input => input.value = '');

      // Update local state with uploaded files
      setProfileData(prev => ({
        ...prev,
        profile: updatedProfile,
        updatedAt: currentDate.toISOString()
      }));

      toast.success('Profile updated successfully!');
      setIsEditing(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  const formatEmploymentType = (type) => {
    if (!type) return '';
    return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-950"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-blue-950">Profile & Resume</h2>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-950 rounded-md hover:bg-[#cddd3a] hover:text-blue-950 transition-colors"
        >
          {isEditing ? 'Cancel' : 'Edit Profile'}
        </button>
      </div>

      {/* Missing Fields Warning */}
      {missingFields.length > 0 && isEditing && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg mb-6">
          <div className="flex items-center gap-2 mb-2">
            <FaExclamationCircle className="text-lg" />
            Please complete the following required fields:
          </div>
          <ul className="list-disc ml-8">
            {missingFields.map((field, index) => (
              <li key={index}>{field}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Profile Status & Visibility Section */}
      <div className="mb-6 p-4 border rounded-lg bg-gray-50">
        <h3 className="font-bold text-lg mb-4">Profile Status & Visibility</h3>

        <div className="mb-4 p-3 border rounded bg-white">
          <div className="flex justify-between items-center">
            <div>
              <label className="font-semibold text-gray-700">Profile Status:</label>
              <p className="text-sm text-gray-600">Based on login activity (system-managed)</p>
            </div>
            <div className="flex items-center">
              {profileData.profileStatus === 'active' ? (
                <>
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                  <span className="font-bold text-green-700">Active</span>
                </>
              ) : (
                <>
                  <span className="inline-block w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                  <span className="font-bold text-red-700">Inactive</span>
                </>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {profileData.lastLoginAt && `Last login: ${new Date(profileData.lastLoginAt).toLocaleDateString()}`}
            {profileData.profileStatus === 'active' &&
              ' (Profile becomes inactive after 90 days without login)'}
          </p>
        </div>

        <div className="p-3 border rounded bg-white">
          <div className="flex justify-between items-center">
            <div>
              <label className="font-semibold text-gray-700">Visibility:</label>
              <p className="text-sm text-gray-600">Control who can see your profile</p>
            </div>
            <ToggleSwitch
              isChecked={profileData.isPublic}
              onChange={handlePrivacyToggle}
              disabled={false}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {profileData.isPublic
              ? 'Your profile is visible to employers. Note: Your profile must be public to apply for jobs.'
              : 'Your profile is hidden from employers'}
          </p>
          <p className="text-xs text-amber-600 mt-1">
            Your profile will not be visible to employers without completing all Required Fields.
          </p>
        </div>

        {profileData.profileStatus === 'inactive' && (
          <div className="mt-3 p-3 bg-yellow-100 border border-yellow-400 rounded">
            <FaExclamationTriangle className="inline mr-2 text-yellow-700" />
            <span className="text-yellow-800">
              Your profile is <strong>inactive</strong> due to 90 days without login.
              Since you're currently logged in, it should reactivate automatically.
            </span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Profile Photo & Video Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Profile Photo */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-lg font-semibold text-blue-950">
              <FaImage />
              <span>Profile Photo <span className="text-sm text-gray-500">(optional)</span></span>
            </div>
            {isEditing ? (
              <div className="space-y-4">
                {profileData.profile?.photo?.url && !files.photo && (
                  <div className="bg-gray-50 p-4 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <img
                        src={profileData.profile.photo.url}
                        alt="Current profile"
                        className="w-16 h-16 object-cover rounded-full"
                      />
                      <div>
                        <p className="font-medium">Current photo</p>
                        <p className="text-sm text-gray-500">{profileData.profile.photo.name}</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'photo')}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer block">
                    <p>{profileData.profile?.photo?.url ? 'Upload a new photo' : 'Click to select your photo'}</p>
                    <p className="text-sm text-gray-500">JPG, PNG, GIF, WEBP (Max 5MB)</p>
                  </label>
                </div>
                {files.photo && (
                  <div className="flex justify-between items-center text-sm text-gray-600 bg-gray-50 p-2 rounded">
                    <div className="flex items-center gap-2">
                      <img
                        src={URL.createObjectURL(files.photo)}
                        alt="Preview"
                        className="w-10 h-10 object-cover rounded-full"
                      />
                      <span>New: {files.photo.name}</span>
                    </div>
                    <button type="button" onClick={() => clearFileSelection('photo')} className="text-red-500 hover:text-red-700">
                      <FaTrash />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg min-h-[200px] flex items-center justify-center">
                {profileData.profile?.photo?.url ? (
                  <img
                    src={profileData.profile.photo.url}
                    alt={`${profileData.firstName} ${profileData.lastName}`}
                    className="max-w-full max-h-[200px] rounded-lg object-cover"
                  />
                ) : (
                  <span className="text-gray-400">No profile photo uploaded</span>
                )}
              </div>
            )}
          </div>

          {/* Video Introduction */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-lg font-semibold text-blue-950">
              <FaVideo />
              <span>Video Introduction <span className="text-sm text-gray-500">(optional)</span></span>
            </div>
            {isEditing ? (
              <div className="space-y-4">
                <input
                  type="text"
                  value={profileData.profile?.video?.youtubeUrl || ''}
                  onChange={(e) => handleProfileChange('video', { youtubeUrl: e.target.value })}
                  placeholder="Enter your YouTube video URL"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500">Paste your YouTube video URL here.</p>
                {profileData.profile?.video?.youtubeUrl && getYouTubeVideoId(profileData.profile.video.youtubeUrl) && (
                  <div className="aspect-video w-full">
                    <iframe
                      className="w-full h-full rounded-lg"
                      src={`https://www.youtube.com/embed/${getYouTubeVideoId(profileData.profile.video.youtubeUrl)}`}
                      title="Video Preview"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg min-h-[200px] flex items-center justify-center">
                {profileData.profile?.video?.youtubeUrl && getYouTubeVideoId(profileData.profile.video.youtubeUrl) ? (
                  <div className="w-full aspect-video">
                    <iframe
                      className="w-full h-full rounded-lg"
                      src={`https://www.youtube.com/embed/${getYouTubeVideoId(profileData.profile.video.youtubeUrl)}`}
                      title="Video Introduction"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <span className="text-gray-400">No video introduction provided</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Resume Document */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold text-blue-950">
            <FaFileAlt />
            <h3>Resume Document <span className="text-red-500">*</span></h3>
          </div>
          {isEditing ? (
            <div className="space-y-4">
              {profileData.profile?.resume?.url && !files.resume && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FaFileAlt className="text-blue-950 text-xl" />
                      <div>
                        <p className="font-medium">{profileData.profile.resume.name}</p>
                        <p className="text-sm text-gray-500">Current resume</p>
                      </div>
                    </div>
                    <a
                      href={profileData.profile.resume.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 text-sm bg-blue-950 text-white rounded-md hover:bg-blue-800 inline-flex items-center"
                    >
                      <FaDownload className="mr-1" /> View
                    </a>
                  </div>
                </div>
              )}
              <div className={`border-2 border-dashed rounded-lg p-6 text-center hover:border-gray-400 transition-colors
                ${!files.resume && !profileData.profile?.resume?.url && missingFields.includes('Resume Document') ? 'border-red-300' : 'border-gray-300'}`}>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange(e, 'resume')}
                  className="hidden"
                  id="resume-upload"
                />
                <label htmlFor="resume-upload" className="cursor-pointer block">
                  <p>{profileData.profile?.resume?.url ? 'Upload a new resume' : 'Click to select your resume'}</p>
                  <p className="text-sm text-gray-500">PDF, DOC, DOCX (Max 10MB)</p>
                </label>
              </div>
              {files.resume && (
                <div className="flex justify-between items-center text-sm text-gray-600 bg-gray-50 p-2 rounded">
                  <span>New: {files.resume.name}</span>
                  <button type="button" onClick={() => clearFileSelection('resume')} className="text-red-500 hover:text-red-700">
                    <FaTrash />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className={`bg-gray-50 p-4 rounded-lg ${!profileData.profile?.resume?.url ? 'ring-2 ring-red-500' : ''}`}>
              {profileData.profile?.resume?.url ? (
                <a
                  href={profileData.profile.resume.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-blue-950 text-white rounded-md hover:bg-[#cddd3a] hover:text-blue-950 transition-colors"
                >
                  <FaFileAlt className="mr-2" />
                  View Resume
                </a>
              ) : (
                <span className="text-red-500">Please upload your resume document</span>
              )}
            </div>
          )}
        </div>

        {/* Personal Information */}
        <div>
          <div className="flex items-center gap-2 text-lg font-semibold text-blue-950 mb-4">
            <FaUser />
            <h3>Personal Information</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={profileData.firstName}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Middle Initial
              </label>
              <input
                type="text"
                name="middleInitial"
                value={profileData.middleInitial}
                onChange={handleChange}
                disabled={!isEditing}
                maxLength={1}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={profileData.lastName}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <div className="flex items-center gap-2 text-lg font-semibold text-blue-950 mb-4">
            <FaEnvelope />
            <h3>Contact Information</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={profileData.email}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={profileData.phone}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
          </div>
        </div>

        {/* Location & Employment */}
        <div>
          <div className="flex items-center gap-2 text-lg font-semibold text-blue-950 mb-4">
            <FaBriefcase />
            <h3>Location & Employment</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Address
              </label>
              <input
                type="text"
                name="address"
                value={profileData.address}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parish <span className="text-red-500">*</span>
              </label>
              <select
                name="parish"
                value={profileData.parish}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="">Select Parish</option>
                <option value="Kingston">Kingston</option>
                <option value="Saint Andrew">Saint Andrew</option>
                <option value="Saint Catherine">Saint Catherine</option>
                <option value="Clarendon">Clarendon</option>
                <option value="Manchester">Manchester</option>
                <option value="Saint Elizabeth">Saint Elizabeth</option>
                <option value="Westmoreland">Westmoreland</option>
                <option value="Hanover">Hanover</option>
                <option value="Saint James">Saint James</option>
                <option value="Trelawny">Trelawny</option>
                <option value="Saint Ann">Saint Ann</option>
                <option value="Saint Mary">Saint Mary</option>
                <option value="Portland">Portland</option>
                <option value="Saint Thomas">Saint Thomas</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employment Type <span className="text-red-500">*</span>
              </label>
              <select
                name="employmentType"
                value={profileData.employmentType}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="">Select Type</option>
                <option value="full-time">Full-time</option>
                <option value="part-time">Part-time</option>
                <option value="contract">Contract</option>
                <option value="temporary">Temporary</option>
                <option value="internship">Internship</option>
                <option value="volunteer">Volunteer</option>
                <option value="freelance">Freelance</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Birth Date
              </label>
              <input
                type="date"
                name="birthDate"
                value={profileData.birthDate}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender
              </label>
              <select
                name="gender"
                value={profileData.gender}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Level of Education
              </label>
              <select
                name="levelOfEducation"
                value={profileData.levelOfEducation}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="">Select Education Level</option>
                <option value="no-formal-education">No Formal Education</option>
                <option value="primary">Primary / Basic School</option>
                <option value="secondary-csec">Secondary (CXC/CSEC)</option>
                <option value="secondary-cape">CAPE / A-Level</option>
                <option value="vocational">Vocational / Technical Certificate</option>
                <option value="associate">Associate Degree</option>
                <option value="bachelor">Bachelor's Degree</option>
                <option value="postgraduate-diploma">Postgraduate Certificate / Diploma</option>
                <option value="master">Master's Degree</option>
                <option value="doctoral">Doctoral Degree (PhD)</option>
              </select>
            </div>
            <div className="flex items-center pt-6">
              <input
                type="checkbox"
                id="labourAvailability"
                name="labourAvailability"
                checked={profileData.labourAvailability === 'yes'}
                onChange={(e) => setProfileData(prev => ({
                  ...prev,
                  labourAvailability: e.target.checked ? 'yes' : 'no'
                }))}
                disabled={!isEditing}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="labourAvailability" className="ml-2 block text-sm text-gray-700">
                Available for Strenuous Physical Labour
              </label>
            </div>
          </div>
        </div>

        {/* Objectives */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Objectives</h3>
          <textarea
            name="objectives"
            value={profileData.objectives}
            onChange={handleChange}
            disabled={!isEditing}
            rows={4}
            placeholder="Describe the types of opportunities you are seeking..."
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
          />
        </div>

        {/* About Me */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">About Me <span className="text-red-500">*</span></h3>
          {isEditing ? (
            <textarea
              value={profileData.profile?.aboutMe || ''}
              onChange={(e) => handleProfileChange('aboutMe', e.target.value)}
              placeholder={aboutMePlaceholder}
              className={`w-full h-48 p-4 border rounded-lg focus:ring-2 focus:ring-blue-500
                ${!profileData.profile?.aboutMe && missingFields.includes('About Me') ? 'border-red-300' : 'border-gray-300'}`}
            />
          ) : (
            <div className={`bg-gray-50 p-4 rounded-lg ${!profileData.profile?.aboutMe ? 'ring-2 ring-red-500' : ''}`}>
              {profileData.profile?.aboutMe ? (
                <p className="whitespace-pre-line text-gray-700">{profileData.profile.aboutMe}</p>
              ) : (
                <span className="text-red-500">Please add an about me section</span>
              )}
            </div>
          )}
        </div>

        {/* Industries of Interest */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Industries of Interest</h3>

          {profileData.industriesOfInterest.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {profileData.industriesOfInterest.map((industry) => (
                <span
                  key={industry}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                >
                  {industry}
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => removeIndustry(industry)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}

          {isEditing && (
            <div className="relative">
              <input
                type="text"
                placeholder="Search and select industries..."
                value={industrySearchTerm}
                onChange={(e) => {
                  setIndustrySearchTerm(e.target.value);
                  setIsIndustryDropdownOpen(true);
                }}
                onFocus={() => setIsIndustryDropdownOpen(true)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />

              {isIndustryDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredIndustries.length === 0 ? (
                    <div className="px-3 py-2 text-gray-500">No industries found</div>
                  ) : (
                    filteredIndustries.map((industry) => (
                      <div
                        key={industry}
                        onClick={() => {
                          toggleIndustry(industry);
                          setIndustrySearchTerm('');
                        }}
                        className={`px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center justify-between ${
                          profileData.industriesOfInterest.includes(industry)
                            ? 'bg-blue-50 text-blue-700'
                            : ''
                        }`}
                      >
                        <span>{industry}</span>
                        {profileData.industriesOfInterest.includes(industry) && (
                          <span className="text-blue-600">✓</span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {isIndustryDropdownOpen && (
                <div
                  className="fixed inset-0 z-0"
                  onClick={() => setIsIndustryDropdownOpen(false)}
                />
              )}
            </div>
          )}

          {profileData.industriesOfInterest.length === 0 && !isEditing && (
            <p className="text-gray-500 text-sm">No industries selected.</p>
          )}
        </div>

        {/* Skills */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Skills <span className="text-red-500">*</span></h3>
            {isEditing && (
              <button
                type="button"
                onClick={handleSkillAdd}
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
              >
                <FaPlus /> <span>Add Skill</span>
              </button>
            )}
          </div>
          {isEditing ? (
            <div className={`space-y-2 ${(!profileData.profile.skills || !profileData.profile.skills.length) && missingFields.includes('At least one skill') ? 'border-red-300 border-2 p-4 rounded-lg' : ''}`}>
              {profileData.profile.skills && profileData.profile.skills.map((skill, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={skill}
                    onChange={(e) => handleSkillChange(index, e.target.value)}
                    className="flex-grow p-2 border rounded"
                    placeholder="Enter skill"
                  />
                  <button
                    type="button"
                    onClick={() => handleSkillDelete(index)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
              {(!profileData.profile.skills || !profileData.profile.skills.length) && (
                <p className="text-red-500 text-sm">Please add at least one skill</p>
              )}
            </div>
          ) : (
            <div className={`bg-gray-50 p-4 rounded-lg ${!profileData.profile?.skills?.length ? 'ring-2 ring-red-500' : ''}`}>
              {profileData.profile?.skills && profileData.profile.skills.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profileData.profile.skills.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-[#cddd3a] text-blue-950 rounded-full text-sm font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-red-500">Please add at least one skill</span>
              )}
            </div>
          )}
        </div>

        {/* Work Experience */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2 text-lg font-semibold text-blue-950">
              <FaBuilding />
              <h3>Work Experience <span className="text-sm text-gray-500">(optional)</span></h3>
            </div>
            {isEditing && (
              <button
                type="button"
                onClick={handleWorkExperienceAdd}
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
              >
                <FaPlus /> <span>Add Experience</span>
              </button>
            )}
          </div>
          {isEditing ? (
            <div className="space-y-4">
              {profileData.profile.workExperience && profileData.profile.workExperience.map((exp, index) => (
                <div key={index} className="space-y-4 p-4 border rounded-lg">
                  <input
                    type="text"
                    value={exp.company || ''}
                    onChange={(e) => handleWorkExperienceChange(index, 'company', e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="Company Name"
                  />
                  <input
                    type="text"
                    value={exp.position || ''}
                    onChange={(e) => handleWorkExperienceChange(index, 'position', e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="Position"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Start Date</label>
                      <input
                        type="text"
                        value={exp.startDate || ''}
                        onChange={(e) => handleWorkExperienceChange(index, 'startDate', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="e.g. Jan 2020"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">End Date</label>
                      <input
                        type="text"
                        value={exp.endDate || ''}
                        onChange={(e) => handleWorkExperienceChange(index, 'endDate', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="e.g. Dec 2022 or Present"
                      />
                    </div>
                  </div>
                  <textarea
                    value={exp.description || ''}
                    onChange={(e) => handleWorkExperienceChange(index, 'description', e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="Job Description"
                    rows="3"
                  />
                  <button
                    type="button"
                    onClick={() => handleWorkExperienceDelete(index)}
                    className="text-red-500 hover:text-red-600 flex items-center space-x-2"
                  >
                    <FaTrash /> <span>Remove</span>
                  </button>
                </div>
              ))}
              {(!profileData.profile.workExperience || profileData.profile.workExperience.length === 0) && (
                <p className="text-gray-500 text-sm italic">No work experience added yet.</p>
              )}
            </div>
          ) : (
            <>
              {profileData.profile?.workExperience && profileData.profile.workExperience.length > 0 ? (
                <div className="space-y-4">
                  {profileData.profile.workExperience.map((exp, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <FaBuilding className="text-blue-950" />
                        <h4 className="text-xl font-semibold text-blue-950">{exp.position}</h4>
                      </div>
                      <div className="text-gray-600">
                        <span className="font-medium">{exp.company}</span>
                        <span className="mx-2">•</span>
                        <span className="flex items-center gap-1 inline">
                          <FaCalendar className="inline text-blue-950" />
                          {exp.startDate} - {exp.endDate || 'Present'}
                        </span>
                      </div>
                      <p className="whitespace-pre-line text-gray-700">{exp.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm italic">No work experience added.</p>
              )}
            </>
          )}
        </div>

        {/* Education */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2 text-lg font-semibold text-blue-950">
              <FaGraduationCap />
              <h3>Education <span className="text-sm text-gray-500">(optional)</span></h3>
            </div>
            {isEditing && (
              <button
                type="button"
                onClick={handleEducationAdd}
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
              >
                <FaPlus /> <span>Add Education</span>
              </button>
            )}
          </div>
          {isEditing ? (
            <div className="space-y-4">
              {profileData.profile.education && profileData.profile.education.map((edu, index) => (
                <div key={index} className="space-y-4 p-4 border rounded-lg">
                  <input
                    type="text"
                    value={edu.institution || ''}
                    onChange={(e) => handleEducationChange(index, 'institution', e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="Institution Name"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={edu.degree || ''}
                      onChange={(e) => handleEducationChange(index, 'degree', e.target.value)}
                      className="w-full p-2 border rounded"
                      placeholder="Degree"
                    />
                    <input
                      type="text"
                      value={edu.field || ''}
                      onChange={(e) => handleEducationChange(index, 'field', e.target.value)}
                      className="w-full p-2 border rounded"
                      placeholder="Field of Study"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Graduation Year</label>
                    <input
                      type="text"
                      value={edu.graduationYear || ''}
                      onChange={(e) => handleEducationChange(index, 'graduationYear', e.target.value)}
                      className="w-full p-2 border rounded"
                      placeholder="e.g. 2022"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleEducationDelete(index)}
                    className="text-red-500 hover:text-red-600 flex items-center space-x-2"
                  >
                    <FaTrash /> <span>Remove</span>
                  </button>
                </div>
              ))}
              {(!profileData.profile.education || profileData.profile.education.length === 0) && (
                <p className="text-gray-500 text-sm italic">No education added yet.</p>
              )}
            </div>
          ) : (
            <>
              {profileData.profile?.education && profileData.profile.education.length > 0 ? (
                <div className="space-y-4">
                  {profileData.profile.education.map((edu, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <FaGraduationCap className="text-blue-950" />
                        <h4 className="text-xl font-semibold text-blue-950">{edu.institution}</h4>
                      </div>
                      <div className="text-gray-600">
                        <span>{edu.degree} in {edu.field}</span>
                        <span className="mx-2">•</span>
                        <span>Class of {edu.graduationYear}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm italic">No education added.</p>
              )}
            </>
          )}
        </div>

        {/* Save Button */}
        {isEditing && (
          <div className="flex justify-end pt-6 border-t">
            <button
              type="submit"
              disabled={saving}
              className={`px-6 py-2 text-white rounded-md transition-colors flex items-center space-x-2 ${
                saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-950 hover:bg-[#cddd3a] hover:text-blue-950'
              }`}
            >
              {saving && <FaSpinner className="animate-spin" />}
              <span>{saving ? 'Saving...' : 'Save All Changes'}</span>
            </button>
          </div>
        )}
      </form>

      {/* Delete Account */}
      <div className="mt-10 pt-6 border-t border-red-200">
        <h3 className="text-lg font-semibold text-red-700 mb-2">Danger Zone</h3>
        <p className="text-sm text-gray-600 mb-4">
          Permanently delete your profile and account. This action cannot be undone.
        </p>
        <button
          type="button"
          onClick={handleDeleteProfile}
          className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
        >
          Delete My Account
        </button>
      </div>
    </div>
  );
};

export default ProfileAndResume;
